import React from "react";
import { Helmet } from "react-helmet";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

const SITE_URL = "https://www.parfimerija-duro.rs";
const SITE_NAME = "DURŌ Niche Perfumery";
const DEFAULT_TITLE = "DURŌ Niche perfumery - Niche parfimerija";
const DEFAULT_DESCRIPTION =
  "DURŌ, where the art of niche perfumery meets ultimate luxury. We represent 30 of the most sought-after artisan perfume houses and offer a one-of-a-kind immersive experience in our boutique.";
const DEFAULT_IMAGE = `${SITE_URL}/assets/images/favicon/og-image.jpg`;
const LOGO_IMAGE = `${SITE_URL}/assets/images/favicon/apple-touch-icon.png`;
const SOCIAL_URLS = [
  "https://www.facebook.com/duro.cologne",
  "https://www.twitter.com/DuroCologne",
  "https://www.instagram.com/duro.cologne",
  "https://www.youtube.com/@duro.cologne",
  "https://www.tiktok.com/@duro.cologne",
];
const BUSINESS_ADDRESS = {
  streetAddress: "Bulevar Vudroa Vilsona 14",
  addressLocality: "Belgrade",
  postalCode: "11000",
  addressCountry: "RS",
};
const BUSINESS_GEO = {
  latitude: 44.8021274,
  longitude: 20.4463451,
};
const BUSINESS_PHONE = "+381 60 5561303";
const BUSINESS_EMAIL = "info@parfimerija-duro.rs";
const BUSINESS_MAP_URL =
  "https://www.google.com/maps/place/DUR%C5%8C+%7C+Niche+perfumery/@44.8021274,20.4463451,17z";
const LOCALE_MAP = {
  en: "en_US",
  sr: "sr_RS",
};

const normalizePath = (path) => {
  if (!path) return "/";
  const trimmed = path.trim();
  if (!trimmed) return "/";
  let normalized = trimmed.startsWith("/") ? trimmed : `/${trimmed}`;
  if (normalized.length > 1 && normalized.endsWith("/")) {
    normalized = normalized.slice(0, -1);
  }
  return normalized;
};

const toAbsoluteUrl = (url) => {
  if (!url) return "";
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  const normalized = url.startsWith("/") ? url : `/${url}`;
  return `${SITE_URL}${normalized}`;
};

const buildTitle = (title) => {
  if (!title || !title.trim()) return DEFAULT_TITLE;
  return `${title} - ${DEFAULT_TITLE}`;
};

export default function Seo({
  title,
  description,
  path,
  image,
  type = "website",
  noindex = false,
  publishedTime,
  modifiedTime,
  alternateLinks = [],
  breadcrumbs = [],
  jsonLd = [],
}) {
  const location = useLocation();
  const { i18n } = useTranslation();
  const language = i18n?.language || "en";

  const normalizedPath = normalizePath(path || location.pathname);
  const canonicalUrl = `${SITE_URL}${normalizedPath}`;
  const resolvedTitle = buildTitle(title);
  const resolvedDescription =
    description && description.trim() ? description : DEFAULT_DESCRIPTION;
  const resolvedImage = toAbsoluteUrl(image || DEFAULT_IMAGE);
  const locale = LOCALE_MAP[language] || language;

  const normalizedAlternates = alternateLinks
    .filter((link) => link?.hrefLang && link?.href)
    .map((link) => ({
      hrefLang: link.hrefLang,
      href: toAbsoluteUrl(link.href),
    }));

  if (normalizedAlternates.length > 0) {
    const hasXDefault = normalizedAlternates.some(
      (link) => link.hrefLang === "x-default"
    );
    if (!hasXDefault) {
      const defaultLink =
        normalizedAlternates.find((link) => link.hrefLang === "en") ||
        normalizedAlternates[0];
      normalizedAlternates.push({
        hrefLang: "x-default",
        href: defaultLink.href,
      });
    }
  }

  const organizationJsonLd = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: SITE_NAME,
    url: SITE_URL,
    logo: LOGO_IMAGE,
  };

  const websiteJsonLd = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: SITE_NAME,
    url: SITE_URL,
    inLanguage: language,
  };
  const localBusinessJsonLd = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    additionalType: "https://schema.org/CosmeticsStore",
    name: SITE_NAME,
    url: SITE_URL,
    image: [DEFAULT_IMAGE],
    logo: LOGO_IMAGE,
    telephone: BUSINESS_PHONE,
    email: BUSINESS_EMAIL,
    address: {
      "@type": "PostalAddress",
      streetAddress: BUSINESS_ADDRESS.streetAddress,
      addressLocality: BUSINESS_ADDRESS.addressLocality,
      postalCode: BUSINESS_ADDRESS.postalCode,
      addressCountry: BUSINESS_ADDRESS.addressCountry,
    },
    geo: {
      "@type": "GeoCoordinates",
      latitude: BUSINESS_GEO.latitude,
      longitude: BUSINESS_GEO.longitude,
    },
    openingHours: ["Mo-Su 10:00-22:00"],
    hasMap: BUSINESS_MAP_URL,
    sameAs: SOCIAL_URLS,
  };

  const breadcrumbJsonLd =
    breadcrumbs.length > 0
      ? {
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: breadcrumbs
            .filter((crumb) => crumb?.name && crumb?.url)
            .map((crumb, index) => ({
              "@type": "ListItem",
              position: index + 1,
              name: crumb.name,
              item: toAbsoluteUrl(crumb.url),
            })),
        }
      : null;

  const jsonLdBlocks = [organizationJsonLd, websiteJsonLd, localBusinessJsonLd]
    .concat(breadcrumbJsonLd ? [breadcrumbJsonLd] : [])
    .concat(Array.isArray(jsonLd) ? jsonLd : [])
    .filter(Boolean);

  return (
    <Helmet htmlAttributes={{ lang: language }}>
      <meta charSet="utf-8" />
      <title>{resolvedTitle}</title>
      <meta name="description" content={resolvedDescription} />
      <meta name="robots" content={noindex ? "noindex, nofollow" : "index, follow"} />

      <link rel="canonical" href={canonicalUrl} />
      {normalizedAlternates.map((link) => (
        <link
          key={`${link.hrefLang}-${link.href}`}
          rel="alternate"
          hrefLang={link.hrefLang}
          href={link.href}
        />
      ))}

      <meta property="og:locale" content={locale} />
      {normalizedAlternates
        .filter(
          (link) => link.hrefLang !== "x-default" && link.hrefLang !== language
        )
        .map((link) => (
          <meta
            key={`og-locale-${link.hrefLang}`}
            property="og:locale:alternate"
            content={LOCALE_MAP[link.hrefLang] || link.hrefLang}
          />
        ))}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={resolvedTitle} />
      <meta property="og:description" content={resolvedDescription} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={resolvedImage} />
      <meta property="og:site_name" content={SITE_NAME} />

      {publishedTime ? (
        <meta property="article:published_time" content={publishedTime} />
      ) : null}
      {modifiedTime ? (
        <meta property="article:modified_time" content={modifiedTime} />
      ) : null}

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={resolvedTitle} />
      <meta name="twitter:description" content={resolvedDescription} />
      <meta name="twitter:image" content={resolvedImage} />

      {jsonLdBlocks.map((block, index) => (
        <script key={`ld-${index}`} type="application/ld+json">
          {JSON.stringify(block)}
        </script>
      ))}
    </Helmet>
  );
}

export { SITE_URL };
